import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Mail, MapPin, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = { name: "", email: "", company: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Sent. We'll get back within 24 hours.");
      setForm(emptyForm);
    } catch (err) {
      const detail =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong. Try again.";
      toast.error(typeof detail === "string" ? detail : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 lg:py-40 bg-[#f4f4f5]"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Left */}
          <div className="lg:col-span-6">
            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-6">
              [ 06 — LET&apos;S BUILD ]
            </div>
            <h2
              data-testid="contact-title"
              className="font-display font-black uppercase tracking-tighter leading-[0.9] text-5xl md:text-6xl lg:text-7xl"
            >
              Got a brand <br />
              worth <span className="text-[#002FA7]">sliding?</span>
            </h2>

            <a
              href="mailto:hello@slideventures.com"
              data-testid="contact-email-link"
              className="group mt-10 inline-flex items-center gap-3 font-display font-black uppercase text-2xl md:text-3xl lg:text-4xl tracking-tight break-all hover:text-[#002FA7] transition-colors"
            >
              hello@slideventures.com
              <ArrowUpRight className="w-6 h-6 md:w-8 md:h-8 transition-transform group-hover:-translate-y-1 group-hover:translate-x-1" />
            </a>

            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-6 border-t border-zinc-200 pt-8">
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 mt-0.5 text-[#002FA7]" strokeWidth={1.5} />
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                    Email
                  </div>
                  <div className="font-medium">hello@slideventures.com</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-0.5 text-[#002FA7]" strokeWidth={1.5} />
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">
                    Studio
                  </div>
                  <div className="font-medium">Remote-first · GMT ↔ PST</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right — Form */}
          <div className="lg:col-span-6 lg:pl-12 lg:border-l border-zinc-200">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="space-y-8"
              noValidate
            >
              <Field
                name="name"
                label="Your name"
                placeholder="Jane Doe"
                value={form.name}
                onChange={onChange}
                testid="contact-input-name"
                required
              />
              <Field
                name="email"
                type="email"
                label="Email"
                placeholder="jane@company.com"
                value={form.email}
                onChange={onChange}
                testid="contact-input-email"
                required
              />
              <Field
                name="company"
                label="Company (optional)"
                placeholder="Company name"
                value={form.company}
                onChange={onChange}
                testid="contact-input-company"
              />

              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
                  Tell us about your project *
                </label>
                <textarea
                  name="message"
                  data-testid="contact-input-message"
                  value={form.message}
                  onChange={onChange}
                  rows={4}
                  required
                  placeholder="Goals, timeline, budget range..."
                  className="w-full bg-transparent border-b-2 border-zinc-300 focus:border-[#002FA7] outline-none py-3 text-lg resize-none transition-colors placeholder:text-zinc-400"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group inline-flex items-center gap-3 bg-[#002FA7] text-white px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-[#001B6B] disabled:opacity-70 disabled:cursor-not-allowed transition-colors duration-300"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Sending
                  </>
                ) : (
                  <>
                    Send inquiry <Send className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ name, type = "text", label, placeholder, value, onChange, testid, required }) {
  return (
    <div>
      <label className="block text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
        {label} {required && "*"}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        data-testid={testid}
        required={required}
        className="w-full bg-transparent border-b-2 border-zinc-300 focus:border-[#002FA7] outline-none py-3 text-lg transition-colors placeholder:text-zinc-400"
      />
    </div>
  );
}
